import React, { useState, useEffect, useRef } from 'react';
import { Brand, Emotion, GenerationParams, Platform, Style } from '../types';
import { ICONS, STYLE_PREVIEWS } from '../constants.tsx';
import GeneratingIdeasScreen from './GeneratingIdeasScreen';

interface Props {
  brand: Brand;
  onStartGeneration: (params: GenerationParams) => void;
  onBack: () => void;
  isGeneratingIdeas: boolean;
  /** Prefill topic when user arrived from landing (handoff). */
  initialTopic?: string;
  /** Reference image from landing (handoff); shown in "מי הכוכב?" and passed through to Editor. */
  initialRefImage?: string | null;
}

// Must match MAX_LENGTHS in functions/src/index.ts (startIdeaGeneration)
const MAX_TOPIC = 500;
const MAX_EXTRA_DETAILS = 500;

const ProjectWizard: React.FC<Props> = ({ brand, onStartGeneration, onBack, isGeneratingIdeas, initialTopic = '', initialRefImage = null }) => {
  const loading = isGeneratingIdeas;
  const [showAssets, setShowAssets] = useState(false); // Collapsible section state

  const [params, setParams] = useState<GenerationParams>({
    topic: initialTopic.slice(0, MAX_TOPIC),
    emotion: Emotion.SHOCK, // Default
    style: Style.REALISTIC,
    extraDetails: '',
    platform: brand.preferredPlatform || Platform.YOUTUBE, // Use brand preference or default to YouTube
    hasSecondPerson: false,
    hasLogo: false,
    characterId: initialRefImage ? undefined : (brand.characters.length > 0 ? brand.characters[0].id : undefined)
  });

  const [customStyleInput, setCustomStyleInput] = useState('');
  
  // Scroll hint logic
  const [showScrollHint, setShowScrollHint] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        // Hide hint if we are close to the bottom
        if (scrollTop + clientHeight >= scrollHeight - 20) {
            setShowScrollHint(false);
        } else {
            setShowScrollHint(true);
        }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'secondPersonImage' | 'logoImage') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setParams(prev => ({ ...prev, [field]: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (!params.topic) {
        alert("נא להזין נושא לסרטון");
        return;
    }
    onStartGeneration(params);
  };

  // Helper to check if the current style is a custom one (not in the standard list excluding OTHER)
  const isStyleCustom = !Object.values(Style).filter(s => s !== Style.OTHER).includes(params.style as Style);

  const handleStyleSelect = (style: Style) => {
      if (style === Style.OTHER) {
          // If selecting Other, keep the current input value if exists, or empty
          setParams({...params, style: (customStyleInput || 'סגנון אישי') as Style});
      } else {
          setParams({...params, style: style});
      }
  };

  const handleCustomStyleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.slice(0, 20); // Limit to 20 chars
      setCustomStyleInput(val);
      setParams({...params, style: val as Style});
  };

  if (loading) {
    return <GeneratingIdeasScreen brandName={brand.name} />;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 md:p-8 pb-20 break-words">
        
        {/* Header */}
        <div className="text-center mb-10 relative">
             <button onClick={onBack} className="absolute right-0 top-1 text-gray-500 hover:text-white">
                <ICONS.ArrowRight className="w-6 h-6" />
            </button>
            <h2 className="text-3xl font-display font-bold text-white mb-1">יצירת פרויקט חדש</h2>
            <p className="text-click-cyan">{brand.name}</p>
        </div>

        <div className="space-y-10">
            
            {/* 1. Platform & Topic - The Core */}
            <div className="space-y-6 bg-click-card p-4 sm:p-6 rounded-2xl border border-click-border">
                
                {/* Platform - Compact Design */}
                <div className="flex justify-center">
                    <div className="inline-flex bg-click-dark rounded-lg p-1 border border-click-border">
                        {[Platform.YOUTUBE, Platform.INSTAGRAM].map(p => (
                            <button
                                key={p}
                                onClick={() => setParams({...params, platform: p})}
                                className={`flex items-center gap-2 px-6 py-2 rounded-md text-sm font-bold transition-all ${
                                    params.platform === p 
                                    ? 'bg-click-border text-click-red shadow-sm' 
                                    : 'text-gray-500 hover:text-white'
                                }`}
                            >
                                {p === Platform.YOUTUBE ? <ICONS.Youtube className="w-4 h-4" /> : <ICONS.Instagram className="w-4 h-4" />}
                                {p}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Topic */}
                <div className="relative">
                     <label className="block text-click-cyan text-sm font-bold mb-2">על מה הסרטון? כתבו בקצרה – אני אחשוב על הרעיונות</label>
                     <input 
                        type="text" 
                        maxLength={MAX_TOPIC}
                        className="w-full bg-click-dark border-2 border-click-border focus:border-click-red text-white p-4 rounded-xl text-xl placeholder-gray-600 transition-colors"
                        placeholder="למשל: טיפים לטיסה זולה, ביקורת משחק, מתכון מהיר"
                        value={params.topic}
                        onChange={e => setParams({...params, topic: e.target.value.slice(0, MAX_TOPIC)})}
                    />
                     <p className="mt-1 text-xs text-gray-500 text-left">{params.topic.length} / {MAX_TOPIC}</p>
                </div>

                {/* Main Character Selection - Promoted to Main Section */}
                <div>
                    <label className="block text-click-cyan text-sm font-bold mb-3">מי הכוכב? (דמות ראשית)</label>
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                        {/* Brand Characters (right in RTL) */}
                        {brand.characters.map(char => (
                            <div 
                                key={char.id}
                                onClick={() => setParams({...params, characterId: char.id})}
                                className={`flex-shrink-0 w-24 h-24 relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                                    params.characterId === char.id 
                                    ? 'border-click-red ring-2 ring-click-red/20' 
                                    : 'border-click-border opacity-60 hover:opacity-100'
                                }`}
                            >
                                {char.images.length > 0 ? (
                                    <img src={char.images[0]} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full bg-click-border flex items-center justify-center"><ICONS.User /></div>
                                )}
                                <div className="bg-black/70 text-white text-caption p-1 text-center absolute bottom-0 w-full truncate">{char.name}</div>
                                
                                {params.characterId === char.id && (
                                    <div className="absolute top-1 right-1 bg-click-red text-black rounded-full p-0.5">
                                        <ICONS.Check className="w-3 h-3" />
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Handoff ref image from landing – use as main character for this project */}
                        {initialRefImage && (
                            <div 
                                onClick={() => setParams({...params, characterId: undefined})}
                                className={`flex-shrink-0 w-24 h-24 relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                                    params.characterId === undefined 
                                    ? 'border-click-red ring-2 ring-click-red/20' 
                                    : 'border-click-border opacity-60 hover:opacity-100'
                                }`}
                            >
                                <img src={initialRefImage} alt="" className="w-full h-full object-cover object-center" />
                                <div className="bg-black/70 text-white text-caption p-1 text-center absolute bottom-0 w-full truncate">תמונה שהעלית</div>
                                {params.characterId === undefined && (
                                    <div className="absolute top-1 right-1 bg-click-red text-black rounded-full p-0.5">
                                        <ICONS.Check className="w-3 h-3" />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Option: No Character (left in RTL) */}
                        <div 
                            onClick={() => setParams({...params, characterId: undefined})}
                            className={`flex-shrink-0 w-24 h-24 relative cursor-pointer rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 ${
                                params.characterId === undefined 
                                ? 'border-click-red bg-click-red/10 text-click-red' 
                                : 'border-click-border bg-click-dark text-gray-500 hover:border-gray-500 hover:text-gray-300'
                            }`}
                        >
                            <ICONS.Image className="w-8 h-8" />
                            <span className="text-caption font-bold">ללא דמות</span>
                            {params.characterId === undefined && (
                                <div className="absolute top-1 right-1 bg-click-red text-black rounded-full p-0.5">
                                    <ICONS.Check className="w-3 h-3" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Extra Details */}
                <div>
                    <label className="block text-click-cyan text-xs font-bold mb-2">מעבר לרעיונות שאני אחשוב עליהם – יש משהו ספציפי שתרצו שיופיע בתמונה? (אופציונלי)</label>
                    <textarea 
                        maxLength={MAX_EXTRA_DETAILS}
                        className="w-full bg-click-dark border border-click-border focus:border-click-red text-white p-3 rounded-lg h-20 text-sm resize-none placeholder-gray-600"
                        placeholder="למשל: רקע אדום, ביגוד מסוים, הבעת פנים דרמטית..."
                        value={params.extraDetails}
                        onChange={e => setParams({...params, extraDetails: e.target.value.slice(0, MAX_EXTRA_DETAILS)})}
                    />
                    <p className="mt-1 text-xs text-gray-500 text-left">{params.extraDetails.length} / {MAX_EXTRA_DETAILS}</p>
                </div>
            </div>

            {/* 2. Vibe (Emotion & Style) */}
            <div className="space-y-8">
                <div>
                    <label className="block text-white text-center text-lg font-bold mb-4">איזה רגש אנחנו רוצים לשדר? 🎭</label>
                    <div className="grid grid-cols-4 gap-3">
                        {Object.values(Emotion).map(em => (
                            <button
                                key={em}
                                onClick={() => setParams({...params, emotion: em})}
                                className={`py-4 px-2 text-caption md:text-sm rounded-xl border transition-all duration-200 transform hover:scale-105 flex items-center justify-center text-center leading-tight ${
                                    params.emotion === em
                                    ? 'bg-click-red text-white border-click-red font-bold shadow-[0_0_10px_rgba(255,0,60,0.3)]'
                                    : 'bg-click-card text-gray-400 border-click-border hover:border-click-red/50 hover:text-white'
                                }`}
                            >
                                {em}
                            </button>
                        ))}
                    </div>
                </div>
                
                <div className="space-y-3">
                    <label className="block text-white text-center text-lg font-bold mb-6">סגנון ויזואלי 🎨</label>
                    
                    {/* Compact Split Layout (Height reduced to 320px for 16:9 feel) */}
                    <div className="flex flex-col md:flex-row gap-4 md:h-[320px]">
                        {/* Right Side: Style List */}
                        <div className="w-full md:w-1/3 relative group/list">
                            <div 
                                ref={scrollContainerRef}
                                onScroll={handleScroll}
                                className="flex flex-col gap-2 overflow-y-auto pl-2 h-[200px] md:h-full pb-32 custom-scrollbar pr-1"
                            >
                                {Object.values(Style).map((style) => {
                                    const isOther = style === Style.OTHER;
                                    const isActive = isOther ? isStyleCustom : params.style === style;

                                    return (
                                        <div key={style} className="relative shrink-0">
                                             <button
                                                onClick={() => handleStyleSelect(style)}
                                                className={`w-full text-right px-4 py-2 rounded-xl transition-all border font-bold flex items-center justify-between group ${
                                                    isActive
                                                    ? 'bg-click-card border-click-red text-white shadow-[0_0_15px_rgba(255,0,60,0.2)]'
                                                    : 'bg-transparent border-transparent hover:bg-click-card/50 text-gray-400 hover:text-white'
                                                }`}
                                            >
                                                <span className="relative z-10 text-sm">{style}</span>
                                                {isActive && <ICONS.ArrowRight className="w-3 h-3 text-click-red rotate-180" />}
                                            </button>
                                            
                                            {/* Inline Input for 'Other' */}
                                            {isOther && isActive && (
                                                <div className="mt-2 mr-4 ml-2 animate-in slide-in-from-top-2">
                                                     <input 
                                                        type="text"
                                                        autoFocus
                                                        value={customStyleInput}
                                                        onChange={handleCustomStyleChange}
                                                        placeholder="הקלד סגנון אישי..."
                                                        className="w-full bg-click-dark border border-click-border focus:border-click-cyan text-white p-2 rounded text-xs"
                                                     />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                             {/* Scroll Hint Overlay */}
                            <div className={`absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-click-dark via-click-dark/90 to-transparent pointer-events-none flex items-end justify-center pb-4 z-10 transition-opacity duration-300 ${showScrollHint ? 'opacity-100' : 'opacity-0'}`}>
                                <div className="bg-click-card/80 p-1 rounded-full border border-click-border/50 backdrop-blur-sm shadow-lg">
                                    <ICONS.ArrowRight className="w-4 h-4 text-gray-400 rotate-90" />
                                </div>
                            </div>
                        </div>

                        {/* Left Side: Large Preview (16:9) */}
                        <div className="w-full md:w-2/3 h-full">
                            <div className="w-full h-full rounded-2xl overflow-hidden border-2 border-click-border relative group shadow-2xl bg-black aspect-video md:aspect-auto">
                                 {/* The Image */}
                                 <img 
                                    src={STYLE_PREVIEWS[isStyleCustom ? Style.OTHER : params.style] || STYLE_PREVIEWS[Style.OTHER]} 
                                    alt={params.style}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-100"
                                 />
                                 
                                 {/* Overlay Info */}
                                 <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-90 flex flex-col justify-end p-8">
                                     <span className="text-click-cyan text-sm font-bold tracking-widest uppercase mb-1 flex items-center gap-2">
                                        <ICONS.Palette className="w-4 h-4" /> תצוגה מקדימה
                                     </span>
                                     <h3 className="text-4xl font-display font-bold text-white shadow-black drop-shadow-lg">
                                         {isStyleCustom && customStyleInput ? customStyleInput : params.style}
                                     </h3>
                                 </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. Advanced Assets (Collapsible) */}
            <div className="border border-click-border rounded-xl overflow-hidden bg-click-card/50 mt-8">
                <button 
                    onClick={() => setShowAssets(!showAssets)}
                    className="w-full p-4 flex items-center justify-between text-gray-300 hover:text-white hover:bg-click-border/30 transition-colors"
                >
                    <span className="font-bold flex items-center gap-2"><ICONS.Plus className="w-4 h-4" /> דמות נוספת ולוגו (מתקדם)</span>
                    <ICONS.ArrowRight className={`w-4 h-4 transition-transform ${showAssets ? '-rotate-90' : 'rotate-90'}`} />
                </button>
                
                {showAssets && (
                    <div className="p-4 border-t border-click-border/50 animate-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-2 gap-4">
                             <div className={`p-4 rounded-lg border border-dashed transition-all ${params.hasSecondPerson ? 'border-click-red bg-click-red/5' : 'border-click-border'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-gray-300">דמות נוספת</span>
                                    <input 
                                        type="checkbox" 
                                        checked={params.hasSecondPerson} 
                                        onChange={e => setParams({...params, hasSecondPerson: e.target.checked})}
                                        className="accent-click-red"
                                    />
                                </div>
                                {params.hasSecondPerson && (
                                     <div className="relative h-20 bg-black/50 rounded flex items-center justify-center cursor-pointer hover:bg-black/70">
                                        {params.secondPersonImage ? (
                                            <img src={params.secondPersonImage} className="h-full object-contain" />
                                        ) : (
                                            <ICONS.Upload className="text-gray-500 w-6 h-6" />
                                        )}
                                        <input type="file" onChange={e => handleImageUpload(e, 'secondPersonImage')} className="absolute inset-0 opacity-0 cursor-pointer" />
                                     </div>
                                )}
                             </div>

                             <div className={`p-4 rounded-lg border border-dashed transition-all ${params.hasLogo ? 'border-click-red bg-click-red/5' : 'border-click-border'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-gray-300">הוספת לוגו</span>
                                    <input 
                                        type="checkbox" 
                                        checked={params.hasLogo} 
                                        onChange={e => setParams({...params, hasLogo: e.target.checked})}
                                        className="accent-click-red"
                                    />
                                </div>
                                {params.hasLogo && (
                                     <div className="relative h-20 bg-black/50 rounded flex items-center justify-center cursor-pointer hover:bg-black/70">
                                        {params.logoImage ? (
                                            <img src={params.logoImage} className="h-full object-contain" />
                                        ) : (
                                            <ICONS.Upload className="text-gray-500 w-6 h-6" />
                                        )}
                                        <input type="file" onChange={e => handleImageUpload(e, 'logoImage')} className="absolute inset-0 opacity-0 cursor-pointer" />
                                     </div>
                                )}
                             </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Generate Button */}
            <button 
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-click-red to-click-red-dim text-white font-display font-black text-2xl py-6 rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_0_30px_rgba(255,0,60,0.3)] flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                START REACTION <ICONS.FlaskConical className="w-8 h-8 text-white fill-white/20" />
            </button>
        </div>

    </div>
  );
};

export default ProjectWizard;